import React, { useEffect, useMemo, useState, useRef } from 'react'
import { Col, Form, Row, Skeleton } from 'antd'
import { filter, find, get, isArray, some } from 'lodash'
import Picker from 'react-mobile-picker'
import styled from 'styled-components'

import { getMenuById, getQuestionsByMenuId } from 'src/useCase/question'
import { HeaderStyle } from 'src/components/layout/Header/HeaderStyle'
import StepsHeader from 'src/components/layout/Header/StepHeader'
import { Wrapper } from 'src/components/layout/Wrapper'
import ButtonCustom from 'src/components/form/Button'
import LPHeader from 'src/components/layout/Header/LPHeader'
import { useAppDispatch, useAppSelector } from 'src/store'
import { QuestionItem } from './styled'
import { useQuestionForm } from './QuestionForm'
import { getInfo, updateUserInfo } from 'src/useCase/user'
import setNotificationActions from 'src/store/ui/notification'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { PATH as PATH_MY_PAGE } from 'src/pages/myPage'
import LocalStorage from 'src/utils/LocalStorage'
import { SELECTED_GENDER_STORAGE_KEY } from 'src/utils/gender'
import MenuNameComponent from 'src/components/common/MenuNameComponent'
import { useRegister } from 'src/containers/Auth/Register/components/useRegisterForm'
import { months, years } from 'src/utils/helper'
import {
  InputLabel,
  LabelRequire,
} from 'src/components/layout/Header/HeaderStyle'
import PrecautionDocument from '../components/Precaution/Document'
import moment from 'moment'
import { trackStep, JOURNEY_STEP } from 'src/utils/tracker'
import {
  clearHiddenSubQuestionsRecursively,
  createCheckQuestionVisibility,
  filterVisibleAnswerPayload,
} from 'src/utils/questionVisibility'

const QuestionContainer = (props: any) => {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const paramsUrl = useParams()
  const [searchParams] = useSearchParams()
  const menuId = searchParams.get('menuId') || paramsUrl?.id
  const from = searchParams.get('from')
  const [isChecked, setIsChecked] = useState(false)
  const [hasReadPrecaution, setHasReadPrecaution] = useState(false)
  const clinicId = searchParams.get('clinicId')
  const [menuName, setMenuName] = useState('')

  const { form, onSubmit: submitQuestion, onValidateForm } = useQuestionForm()
  const { setValue, formState: qFormState, setError } = form

  const userInfo = useAppSelector((state) => state.ui.app.authInfo)

  const userClinicId = userInfo?.clinics?.[0]?.id
  const isClinicMismatch = Boolean(
    userInfo &&
      clinicId &&
      userClinicId &&
      Number(clinicId) !== Number(userClinicId),
  )

  useEffect(() => {
    if (isClinicMismatch) {
      navigate(PATH_MY_PAGE.OTHER_MENU)
    }
  }, [isClinicMismatch, navigate])
  const userTreatment = useMemo(() => {
    return find(
      userInfo?.user_treatments || [],
      (userTreatment) => userTreatment?.menu_id == paramsUrl?.id,
    )
  }, [userInfo, paramsUrl?.id])
  const [isSubmittingAll, setIsSubmittingAll] = useState(false)

  let reservation = userTreatment?.reservation

  if (!reservation && userTreatment?.reservations) {
    reservation =
      find(
        userTreatment?.reservations,
        (reservation) => reservation?.answers?.length === 0,
      ) || userTreatment?.reservations[userTreatment?.reservations?.length - 1]
  }

  const userTreatmentRef = useRef(userTreatment)
  const reservationRef = useRef(reservation)
  const birthdayRef = useRef<HTMLDivElement>(null)
  const draftSanitizedRef = useRef(false)

  useEffect(() => {
    draftSanitizedRef.current = false
  }, [menuId])

  useEffect(() => {
    userTreatmentRef.current = userTreatment
    reservationRef.current = reservation
  }, [userTreatment, reservation])

  const { form: userForm, onSubmit: submitUserInfo } = useRegister()

  const [isShowDatePicker, setIsShowDatePicker] = useState(false)
  const [birthDay, setBirthDay] = useState(
    userInfo?.date_of_birth || '生年月日を選択してください',
  )
  const [birthdayError, setBirthdayError] = useState<string | null>(null)

  useEffect(() => {
    if (userInfo?.date_of_birth) {
      setBirthDay(userInfo.date_of_birth)
    }
  }, [userInfo?.date_of_birth])

  const renderDays = (
    currentYear: string = '1980年',
    currentMonth: string = '1月',
  ) => {
    const lastDay = new Date(
      parseInt(currentYear),
      parseInt(currentMonth),
      0,
    ).getDate()

    return Array.from({ length: lastDay }, (_, index) => {
      return `${index + 1}日`
    })
  }

  const [valueGroups, setValueGroups] = useState({
    year: '1980年',
    month: '1月',
    day: '1日',
  })
  const [optionGroups, setOptionGroups] = useState({
    year: years(),
    month: months(),
    day: renderDays(valueGroups.year, valueGroups.month),
  })

  const [submitData, setSubmitData] = useState({
    answers: [],
    reservation_id: '',
    menu_id: 0,
  })

  const questionList = useAppSelector(
    (state) => state.domain.automaticPlan.questions.entity,
  )
  const isLoadingQuestionList =
    useAppSelector((state) => state.domain.automaticPlan.questions.loading) ||
    false
  const questionObject =
    questionList &&
    questionList.length > 0 &&
    questionList.reduce((a, v, key) => ({ ...a, [`${key + 1}`]: v }), {})

  const questionLabels = useMemo(() => {
    if (!questionList || questionList.length === 0) return {}
    const labels: Record<string, string> = {}
    let rootCount = 0
    const parentSubCounts: Record<string, number> = {}

    questionList.forEach((q: any) => {
      const qId = String(q.id || q.temp_id)
      if ((q.depth || 0) === 0) {
        rootCount++
        labels[qId] = `${rootCount}`
      } else {
        const parentId = q.parent_question_id || q.parent_temp_id
        const parentLabel = labels[parentId] || '?'
        if (!parentSubCounts[parentId]) {
          parentSubCounts[parentId] = 0
        }
        parentSubCounts[parentId]++
        labels[qId] = `${parentLabel}.${parentSubCounts[parentId]}`
      }
    })
    return labels
  }, [questionList])

  const questionRefs = useMemo(
    () => ({} as Record<string, HTMLDivElement | null>),
    [],
  )

  const isSubmitDisabled = !(isChecked && hasReadPrecaution)

  const checkVisibility = useMemo(
    () => createCheckQuestionVisibility(questionList),
    [questionList],
  )

  useEffect(() => {
    if (
      !questionList?.length ||
      isLoadingQuestionList ||
      draftSanitizedRef.current
    ) {
      return
    }
    draftSanitizedRef.current = true
    const values = { ...form.getValues() }
    clearHiddenSubQuestionsRecursively(questionList, values, (name, value) => {
      setValue(name as any, value)
    })
  }, [questionList, isLoadingQuestionList, form, setValue])

  useEffect(() => {
    setOptionGroups((prev) => ({
      ...prev,
      day: renderDays(valueGroups.year, valueGroups.month),
    }))
  }, [valueGroups])

  const handleChangeDate = (name: string, value: string) => {
    setValueGroups({
      ...valueGroups,
      [name]: value,
    })
  }

  const showDatePicker = () => {
    setIsShowDatePicker(true)
    document.body.classList.add('stop-scroll')
  }

  const cancelDatePicker = () => {
    document.body.classList.remove('stop-scroll')
    setIsShowDatePicker(false)
  }

  const confirmBirthday = () => {
    document.body.classList.remove('stop-scroll')
    setIsShowDatePicker(false)
    const birthdayFormat = moment({
      year: parseInt(valueGroups.year),
      month: parseInt(valueGroups.month) - 1,
      day: parseInt(valueGroups.day),
    }).format('YYYY-MM-DD')
    setBirthDay(birthdayFormat)
    userForm.setValue('date_of_birth', birthdayFormat)
    setBirthdayError(null)
  }

  useEffect(() => {
    if (!menuName && menuId && clinicId) {
      dispatch(
        getMenuById({
          menuId,
          params: {
            clinic_id: clinicId,
            select_field:
              'id,name,status,created_at,updated_at,is_default,image_url,target_gender',
          },
        }),
      )
        .then((res: any) => {
          const menu = res.payload
          // setMenuData(menu)
          setMenuName(menu?.name || '')
        })
        .catch(() => setMenuName(''))
    }
  }, [menuId, clinicId, dispatch, menuName])

  // Tracks the last menuId for which QUESTIONNAIRE_STARTED was fired.
  // Intentional: re-fires when the user selects a different menu in the same session.
  // Does not re-fire on re-renders with the same menuId.
  const trackedMenuIdRef = useRef<string | null>(null)

  useEffect(() => {
    if (menuId) {
      if (trackedMenuIdRef.current !== menuId) {
        trackedMenuIdRef.current = menuId
        trackStep(JOURNEY_STEP.QUESTIONNAIRE_STARTED, {
          metadata: { menu_id: menuId },
        })
      }
      dispatch(getQuestionsByMenuId({ menu_id: menuId }))
    }
  }, [menuId, dispatch])

  useEffect(() => {
    if (from === 'LP') {
      return
    }
    LocalStorage.removePrecaution()
    dispatch(
      getInfo({
        with: 'userTreatments,userTreatments.reservations,userTreatments.reservations.answers,userTreatments.userTreatmentTransitions',
      }),
    )
  }, [dispatch, from])

  const handleChange = (name: any, type: string, questionAnswer: any) => {
    if (type === 'checkbox') {
      if (
        questionAnswer &&
        !some(
          form.watch(name),
          (checkboxValue) => checkboxValue.value === questionAnswer.value,
        )
      ) {
        const formValue = form.watch(name)
        if (formValue) {
          setValue(name, [...formValue, questionAnswer])
          setError(name, {})
        } else {
          setValue(name, [questionAnswer])
          setError(name, {})
        }
      } else {
        setValue(name, [
          ...filter(
            form.watch(name),
            (data) => data.value !== questionAnswer.value,
          ),
        ])
        setError(name, {})
      }
    } else {
      setValue(name, questionAnswer)
      setError(name, {})
    }
    const data = form.getValues()
    clearHiddenSubQuestionsRecursively(questionList, data, (name, value) => {
      setValue(name as any, value)
    })

    let answerData: any = []

    data &&
      Object.entries(data).map((formValue) => {
        const data =
          questionObject &&
          find(
            questionObject,
            (data) => (get(data, 'id') || get(data, 'temp_id')) == formValue[0],
          )
        const questionData = questionObject
          ? data
          : { type: '', id: '', temp_id: '' }
        const value = get(formValue, '1', null)

        if (questionData) {
          const questionType = questionData.type

          let valueInput = {
            is_call_video: false,
            value: value,
            count_number: get(questionData, 'options.0.count_number', 0),
          }
          let nextAnswer: any = {
            answer:
              questionType === 'radio'
                ? value
                  ? [value]
                  : []
                : questionType === 'checkbox'
                ? value || []
                : [valueInput],
            question_id: questionData.id,
            temp_id: questionData.temp_id,
            question_type: questionData.type,
          }

          answerData = [...answerData, nextAnswer]
        }
      })

    answerData = filterVisibleAnswerPayload(questionList, answerData)

    setSubmitData((prev) => {
      const reservationId = reservation?.id ?? null
      return {
        ...prev,
        answers: answerData,
        reservation_id: reservationId,
        menu_id: Number(menuId) || 0,
      }
    })

    return
  }

  const handleClick = () => setIsChecked((prev) => !prev)

  const handleSubmitAll = async () => {
    if (isSubmittingAll) return
    setIsSubmittingAll(true)

    try {
      if (
        !userInfo?.date_of_birth &&
        (!birthDay || birthDay === '生年月日を選択してください')
      ) {
        setBirthdayError('生年月日は必須項目です。選択してください。')
        setIsSubmittingAll(false)
        if (birthdayRef.current) {
          birthdayRef.current.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          })
        }
        return
      }

      if (!userInfo) {
        const userValid = await userForm.trigger()
        if (!userValid) {
          const err = userForm.formState.errors
          const firstErrName = Object.keys(err)[0] as any
          if (firstErrName) userForm.setFocus(firstErrName)
          return
        }
      }

      const { valid: questionValid, firstErrorId } = await onValidateForm(
        submitData,
      )
      if (!questionValid) {
        const firstErrName = firstErrorId as any
        if (firstErrName) {
          form.setFocus(firstErrName)

          const target = questionRefs[firstErrName]
          if (target) {
            target.scrollIntoView({
              behavior: 'smooth',
              block: 'center',
            })
          }
        }
        return
      }

      const userData = userForm.getValues()

      let latestUserInfo = userInfo
      if (!userInfo) {
        const ok = await submitUserInfo(userData, '', null)
        if (!ok) return
      } else if (!userInfo.date_of_birth) {
        try {
          latestUserInfo = await dispatch(
            updateUserInfo({
              family_name: userInfo.family_name,
              given_name: userInfo.given_name,
              family_name_kana: userInfo.family_name_kana,
              given_name_kana: userInfo.given_name_kana,
              date_of_birth: birthDay,
            }),
          ).unwrap()
        } catch (error: any) {
          const apiErrorMessage =
            error?.errors?.date_of_birth?.[0] ||
            error?.message ||
            '情報の更新に失敗しました。'
          setBirthdayError(apiErrorMessage)
          dispatch(
            setNotificationActions.onNotification({
              type: 'error',
              message: apiErrorMessage,
            }),
          )
          setIsSubmittingAll(false)
          return
        }
      }

      // Try to find the latest treatment and reservation from the API response
      // if it was an update call. Fallback to refs for registration/other cases.
      const latestUserTreatment = find(
        latestUserInfo?.user_treatments || [],
        (t: any) => t?.menu_id == menuId,
      )

      let latestReservation = latestUserTreatment?.reservation
      if (!latestReservation && latestUserTreatment?.reservations) {
        latestReservation =
          find(
            latestUserTreatment?.reservations,
            (r: any) => r?.answers?.length === 0,
          ) ||
          latestUserTreatment?.reservations[
            latestUserTreatment?.reservations?.length - 1
          ]
      }

      const reservationId = latestReservation?.id || reservationRef.current?.id
      const userTreatmentId =
        latestUserTreatment?.id || userTreatmentRef.current?.id

      if (!reservationId) {
        setIsSubmittingAll(false)
        return
      }

      const finalSubmitData = {
        ...submitData,
        reservation_id: reservationId,
        user_treatment_id: userTreatmentId,
        menu_id: Number(menuId) || 0,
      }
      if (!(await submitQuestion(finalSubmitData))) return

      trackStep(JOURNEY_STEP.QUESTIONNAIRE_COMPLETED, {
        metadata: { menu_id: menuId },
      })
      LocalStorage.removeLocalStorage(SELECTED_GENDER_STORAGE_KEY)
    } finally {
      setIsSubmittingAll(false)
    }
  }

  if (isLoadingQuestionList) {
    return <Skeleton />
  }

  return (
    <>
      <LPHeader />
      <Wrapper className="w-full justify-center mx-auto">
        <StepsHeader
          step={10}
          // stepActive={2}
          stepActiveName={'問診・注意事項確認'}
          className="top-70"
        />
        <div>
          {!userInfo ? (
            <HeaderStyle>ご利用者情報入力</HeaderStyle>
          ) : (
            <>
              <HeaderStyle>問診票</HeaderStyle>
              <MenuNameComponent className="mb-25" menuNameProp={menuName} />
            </>
          )}
        </div>

        {isLoadingQuestionList ? (
          <div className="mx--20">
            <Skeleton />
          </div>
        ) : (
          <Form className="pb-20">
            {!userInfo?.date_of_birth && (
              <BirthdayPickerWrapper ref={birthdayRef}>
                <Row>
                  <Col xs={24}>
                    <Row className="justify-content-between">
                      <InputLabel className="font-weight-bold">
                        生年月日
                      </InputLabel>
                      <LabelRequire>
                        <span>必須</span>
                      </LabelRequire>
                    </Row>

                    <div
                      className={`ant-form-item input-birthday ${
                        birthdayError ? 'ant-form-item-has-error' : ''
                      }`}
                    >
                      <div onClick={showDatePicker}>
                        <p className="ant-input aga-input">{birthDay}</p>
                      </div>
                      {birthdayError && (
                        <div
                          className="ant-form-item-explain ant-form-item-explain-error"
                          style={{
                            color: '#ff4d4f',
                            fontSize: '14px',
                            marginTop: '4px',
                          }}
                        >
                          {birthdayError}
                        </div>
                      )}
                    </div>
                    {isShowDatePicker && (
                      <div className="picker-group">
                        <div className="picker-group__item">
                          <div className="d-flex justify-content-space-between birth-action">
                            <b onClick={cancelDatePicker} className="btn-gray">
                              キャンセル
                            </b>
                            <b
                              className="btn-warning"
                              onClick={confirmBirthday}
                            >
                              OK
                            </b>
                          </div>
                          <div className="d-flex">
                            <Picker
                              optionGroups={optionGroups}
                              valueGroups={valueGroups}
                              onChange={handleChangeDate}
                              wheel="normal"
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  </Col>
                </Row>
              </BirthdayPickerWrapper>
            )}
            {questionList &&
              questionList.length > 0 &&
              questionList.map((question, index) => {
                const qName: any = `${question.id || question.temp_id}`
                const questionName =
                  question.question && question.question.split('\\n')

                // Check visibility for sub-questions
                const allAnswers = form.watch()
                const isVisible = checkVisibility(question, allAnswers)

                let maxLengthOption = 0
                question.options &&
                  isArray(question.options) &&
                  question.options.map((option) => {
                    const valLength = option?.value?.length || 0
                    if (valLength > Number(maxLengthOption)) {
                      maxLengthOption = valLength
                    }
                    return option
                  })

                if (maxLengthOption < 30) {
                  maxLengthOption = 30
                }

                return (
                  <QuestionItem
                    key={question.id || question.temp_id}
                    depth={question.depth || 0}
                    ref={(el) => {
                      questionRefs[question.id || (question.temp_id as any)] =
                        el
                    }}
                    className={`${
                      form.getFieldState(qName)?.error &&
                      Object.keys(
                        form.getFieldState(qName)?.error || {},
                      ).filter((key) => key !== 'ref').length > 0
                        ? 'has-error'
                        : ''
                    } ${!isVisible ? 'is-hidden' : ''}`}
                  >
                    <div className="q-title">
                      <div className="q-left">
                        Q
                        {questionLabels[
                          String(question.id || question.temp_id)
                        ] || index + 1}{' '}
                        <span className="pre-wrap">
                          {questionName &&
                            questionName.map(function (item, idx) {
                              return (
                                <span key={idx}>
                                  {item}
                                  <br />
                                </span>
                              )
                            })}
                        </span>
                      </div>
                      {question.required && (
                        <span className="required">必須</span>
                      )}
                    </div>
                    {question.type === 'checkbox' && (
                      <>
                        <div className="q-wrap">
                          {Array.isArray(question.options) &&
                            question.options.length > 0 &&
                            question.options.map((item, index) => {
                              const isChecked =
                                form.watch(qName) &&
                                some(
                                  form.watch(qName),
                                  (checkboxValue) =>
                                    checkboxValue.value === item.value,
                                )

                              return (
                                <label
                                  className="label-q c-pointer"
                                  key={index}
                                >
                                  <input
                                    type="checkbox"
                                    placeholder="January"
                                    name={qName}
                                    className="btn-q mx-3"
                                    onChange={(event) =>
                                      handleChange(
                                        event.target.name,
                                        'checkbox',
                                        item,
                                      )
                                    }
                                    checked={!!isChecked}
                                    value={item.value}
                                    id={`${qName}-${item.value}-${index}`}
                                  />
                                  <span
                                    style={{
                                      padding: `${
                                        maxLengthOption / 2.4 + 10
                                      }px 0`,
                                    }}
                                  >
                                    {item.value}
                                  </span>
                                </label>
                              )
                            })}
                        </div>
                        {form.getFieldState(qName) &&
                          form.getFieldState(qName).error?.message && (
                            <div className="error-text">選択してください。</div>
                          )}
                      </>
                    )}
                    {question.type === 'radio' && (
                      <>
                        <div className="q-wrap">
                          {Array.isArray(question.options) &&
                            question.options.length > 0 &&
                            question.options.map((item, index) => {
                              return (
                                <label
                                  className="label-q c-pointer"
                                  key={index}
                                >
                                  <input
                                    type="radio"
                                    placeholder="January"
                                    name={qName}
                                    className="btn-q mx-3"
                                    checked={
                                      !!(
                                        form.watch(qName) &&
                                        form.watch(qName).value === item.value
                                      )
                                    }
                                    onChange={(event) =>
                                      handleChange(
                                        event.target.name,
                                        'radio',
                                        item,
                                      )
                                    }
                                    value={item.value}
                                    id={`${qName}-${item.value}`}
                                  />
                                  <span
                                    style={{
                                      padding: `${
                                        maxLengthOption / 2.4 + 10
                                      }px 0`,
                                    }}
                                  >
                                    {item.value}
                                  </span>
                                </label>
                              )
                            })}
                        </div>
                        {form.getFieldState(qName) &&
                          form.getFieldState(qName).error?.message && (
                            <div className="error-text">選択してください。</div>
                          )}
                      </>
                    )}
                    <div className="px--20 mb-20">
                      {question.type === 'textarea' && (
                        <>
                          <textarea
                            name={qName}
                            className="aga-input question-input mb-10 px-10 aga-input-area"
                            placeholder={
                              question.placeholder || '入力してください'
                            }
                            value={form.watch(qName) || ''}
                            onChange={(event) =>
                              handleChange(
                                event.target.name,
                                'textarea',
                                event.target.value,
                              )
                            }
                          />
                          {form.getFieldState(qName) &&
                            form.getFieldState(qName).error?.message && (
                              <div className="error-text mt-e-20 px--0">
                                入力してください。
                              </div>
                            )}
                        </>
                      )}
                    </div>
                    <div className="px--20 mb-20">
                      {question.type === 'input' && (
                        <>
                          <input
                            name={qName}
                            className="aga-input question-input mb-10 px-10"
                            placeholder={
                              question.placeholder || '入力してください'
                            }
                            value={form.watch(qName) || ''}
                            onChange={(event) =>
                              handleChange(
                                event.target.name,
                                'input',
                                event.target.value,
                              )
                            }
                          />
                          {form.getFieldState(qName) &&
                            form.getFieldState(qName).error?.message && (
                              <div className="error-text mt-e-20 px--0">
                                入力してください。
                              </div>
                            )}
                        </>
                      )}
                    </div>
                  </QuestionItem>
                )
              })}
            <HeaderStyle>注意事項の確認</HeaderStyle>
            <PrecautionDocument setHasReadPrecaution={setHasReadPrecaution} />
            <div className="mt-3">
              <b>
                利用規約を最後までスクロールすると、以下利用規約への同意にチェックができます。
              </b>
            </div>
            <CustomCheckbox>
              <label className="checkbox-label checkbox-all mt-3">
                <div>
                  <input
                    type="checkbox"
                    onChange={() => handleClick()}
                    className={`
                  checkbox-checkmark ${isChecked ? 'checked' : ''} ${
                      !hasReadPrecaution ? 'disabled' : ''
                    }`}
                    checked={isChecked}
                    disabled={!hasReadPrecaution}
                  />
                  <span
                    className={`
                    checkbox-checkmark ${isChecked ? 'checked' : ''} ${
                      !hasReadPrecaution ? 'disabled' : ''
                    }
                  `}
                  ></span>
                  <span>
                    上記の全ての利用規約（はじめにご確認いただく事項ならびに、利用規約第3条に記載のリンク先「医薬品リスト」も確認し、内容についても理解しています）に同意します。
                  </span>
                </div>
              </label>
            </CustomCheckbox>
            <div className="p-20">
              <ButtonCustom
                className="btn-default btn-chestnut-rose"
                loading={
                  isSubmittingAll ||
                  userForm.formState.isSubmitting ||
                  qFormState.isSubmitting
                }
                disabled={isSubmitDisabled}
                onClick={handleSubmitAll}
                name="次へ進む"
                showIconRight
              />
            </div>
          </Form>
        )}
      </Wrapper>
    </>
  )
}

const BirthdayPickerWrapper = styled.div`
  .input-birthday {
    .aga-input {
      font-size: 16px;
    }
  }

  .picker-group {
    position: fixed;
    width: 100%;
    bottom: 0;
    left: 0;
    height: 100vh;
    background: #000000a8;
    z-index: 100;

    &__item {
      position: absolute;
      width: 100%;
      background-color: #fff;
      bottom: 0;
      left: 0;
      font-size: 16px;
      border-radius: 10px 10px 0 0;

      .birth-action {
        background-color: #ffffff;
        padding: 15px;
        border-bottom: 1px solid #998800;
        border-radius: 10px 10px 0 0;
        justify-content: space-between;

        .btn-warning {
          color: var(--color-dusty-gray);
          background-color: var(--color-white);
          cursor: pointer;
        }

        .btn-gray {
          color: #999999;
          cursor: pointer;
        }
      }
    }
  }

  .picker-date {
    background-color: #f1efe7;
    border: 1px solid #cfc392;
    padding: 10px;
  }

  .picker-inner {
    -webkit-mask-box-image-source: none !important;
  }

  .picker-container .picker-column .picker-item.picker-item-selected {
    font-weight: bold;
  }
`

const CustomCheckbox = styled.div`
  .checkbox-label {
    display: block;
    position: relative;
    padding-left: 15px;
    font-style: normal;
    font-weight: 500;
    font-size: 13px;
    line-height: 20px;
    color: #000000;
    user-select: none;
    border-bottom: 1px solid #f1efe7;
    padding-bottom: 15px;
    margin-top: 15px;
    cursor: pointer;

    &.checkbox-all {
      padding-left: 40px;
      border-bottom: none;
      display: inline-flex;
      align-items: center;

      span {
        line-height: 32px;
      }
    }

    .checkbox-title {
      a {
        color: hsl(209, 100%, 37.5%);
      }
    }
    .checkbox-checkmark:after {
      left: 8px;
      top: 3px;
      width: 10px;
      height: 18px;
      border: solid white;
      border-width: 0 3px 3px 0;
      transform: rotate(45deg);
    }
    input {
      position: absolute;
      opacity: 0;
      cursor: pointer;
      height: 0;
      width: 0;
    }
    .checkbox-checkmark {
      position: absolute;
      top: 0;
      left: 0;
      height: 28px;
      width: 28px;
      background-color: var(--color-spring-wood);
      border-radius: 5px;
    }
    .checkbox-checkmark:after {
      content: '';
      position: absolute;
      display: none;
    }

    .disabled {
      cursor: not-allowed;
    }
  }
  .checked {
    background-color: var(--color-yuma) !important;
    border: 1px solid var(--color-yuma) !important;
  }
  .checkbox-checkmark {
    &.checked:after {
      display: block;
    }
  }
`

export default QuestionContainer
